import type { GameEvent } from '../schema';

/** Milestones, retirement, legacy, and the closing arc. */
export const MILESTONE_EVENTS: GameEvent[] = [
  {
    id: 'milestone.eighteen',
    title: 'Everything is unassigned',
    description: 'For the first time, nobody is going to tell you what happens next.',
    category: 'life',
    pools: ['pool.milestone.launch'],
    weight: 10,
    unique: true,
    ageMax: 20,
    choices: [
      {
        id: 'plan',
        label: 'Make a plan',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Made a plan',
            narrative: 'Most of it will not survive contact with the decade. Having one still helps.',
            tone: 'positive',
            effects: [
              { k: 'trait', trait: 'conscientiousness', delta: 5 },
              { k: 'skill', skill: 'selfManagement', delta: 4 },
              { k: 'stat', stat: 'purpose', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'explore',
        label: 'See what happens',
        hint: 'mixed',
        tags: ['exploration'],
        outcomes: [
          {
            weight: 1,
            label: 'Improvised',
            narrative: 'Open to everything, committed to nothing. It is a strategy, briefly.',
            tone: 'neutral',
            effects: [
              { k: 'trait', trait: 'noveltySeeking', delta: 6 },
              { k: 'trait', trait: 'adaptability', delta: 5 },
              { k: 'stat', stat: 'happiness', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'milestone.twentyEight',
    title: 'The first real accounting',
    description:
      'Your peers have visibly diverged: some are senior, some are married, some are lost. You are one of them.',
    category: 'life',
    pools: ['pool.milestone.28'],
    weight: 10,
    unique: true,
    choices: [
      {
        id: 'compare',
        label: 'Measure yourself against them',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Compared',
            narrative: 'Motivating and corrosive in roughly equal measure.',
            tone: 'neutral',
            effects: [
              { k: 'trait', trait: 'ambition', delta: 7 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'stat', stat: 'stress', delta: 6 },
              { k: 'career', op: 'adjust', field: 'performance', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'own',
        label: 'Define your own scoreboard',
        hint: 'safe',
        tags: ['reflection'],
        outcomes: [
          {
            weight: 1,
            label: 'Chose your own measures',
            narrative: 'Harder than it sounds, and worth more than any single year of salary.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 11 },
              { k: 'stat', stat: 'happiness', delta: 7 },
              { k: 'trait', trait: 'resilience', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'milestone.thirtyFive',
    title: 'The shape is visible',
    description: 'Enough has happened that the trajectory is no longer hypothetical.',
    category: 'life',
    pools: ['pool.milestone.35'],
    weight: 10,
    unique: true,
    choices: [
      {
        id: 'double',
        label: 'Commit harder to this path',
        hint: 'mixed',
        tags: ['ambition'],
        outcomes: [
          {
            weight: 1,
            label: 'Committed',
            narrative: 'Depth over optionality. A real trade, made knowingly.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: 8 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
              { k: 'aspiration', op: 'progress', defId: 'goal.mastery', delta: 30 },
              { k: 'trait', trait: 'adaptability', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'broaden',
        label: 'Keep your options open',
        hint: 'mixed',
        tags: ['exploration'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed broad',
            narrative: 'Flexible, employable, and slightly less exceptional at any one thing.',
            tone: 'neutral',
            effects: [
              { k: 'skill', skill: 'communication', delta: 5 },
              { k: 'skill', skill: 'business', delta: 4 },
              { k: 'trait', trait: 'adaptability', delta: 6 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: -2 },
            ],
          },
        ],
      },
      {
        id: 'balance',
        label: 'Protect the non-work part',
        hint: 'safe',
        tags: ['balance', 'family'],
        outcomes: [
          {
            weight: 1,
            label: 'Chose balance',
            narrative: 'You capped the career deliberately and spent the difference on people.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', dim: 'closeness', delta: 10 },
              { k: 'habit', habit: 'relationshipCare', delta: 12 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -8 },
              { k: 'burnout', op: 'load', delta: -8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'milestone.fiftyFive',
    title: 'The last long runway',
    description: 'A decade, perhaps a little more, in which large changes are still genuinely possible.',
    category: 'life',
    pools: ['pool.milestone.55'],
    weight: 10,
    unique: true,
    choices: [
      {
        id: 'maximise',
        label: 'Maximise the final earning years',
        hint: 'mixed',
        tags: ['finance', 'ambition'],
        outcomes: [
          {
            weight: 1,
            label: 'Pushed the earnings',
            narrative: 'The last decade of full salary did a disproportionate amount of work on the retirement figure.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: 10 },
              { k: 'habit', habit: 'saving', delta: 14 },
              { k: 'retirement', mode: 'add', value: 0 },
              { k: 'counter', name: 'retirementBoost', delta: 1 },
              { k: 'burnout', op: 'load', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'unfinished',
        label: 'Do the thing you never did',
        hint: 'risky',
        tags: ['courage', 'late'],
        outcomes: [
          {
            weight: 1,
            label: 'Finally did it',
            narrative: 'It cost money and dignity and produced the best two years in the file.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 20 },
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'counter', name: 'lateChanges', delta: 1 },
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'cashIncomeMonths', months: -4, note: 'the unfinished thing' },
              { k: 'regret', op: 'resolve', id: 'regret.craft' },
              { k: 'regret', op: 'resolve', id: 'regret.business' },
              { k: 'memory', title: 'Did the thing you had always postponed', tone: 'milestone', category: 'life', weight: 9 },
            ],
          },
        ],
      },
      {
        id: 'people',
        label: 'Put it all into people',
        hint: 'safe',
        tags: ['family', 'contribution'],
        outcomes: [
          {
            weight: 1,
            label: 'Chose people',
            narrative: 'Children, friends, a community role. The returns were immediate and non-financial.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', dim: 'closeness', delta: 14 },
              { k: 'relationshipAll', dim: 'trust', delta: 8 },
              { k: 'counter', name: 'contributions', delta: 2 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'stat', stat: 'purpose', delta: 10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'milestone.sixtyFour',
    title: 'The decision you have been avoiding',
    description: 'You could stop. Whether you can afford to, and whether you want to, are different questions.',
    category: 'life',
    pools: ['pool.milestone.64', 'pool.retirement'],
    weight: 10,
    unique: true,
    choices: [
      {
        id: 'retire',
        label: 'Retire',
        hint: 'mixed',
        requires: [{ k: 'careerStatus', anyOf: ['employed', 'selfEmployed', 'founder', 'break'] }],
        showWhenLocked: true,
        tags: ['retirement'],
        outcomes: [
          {
            weight: 1,
            label: 'Retired',
            narrative: 'A cake, a card, and an unstructured Monday that will take a year to get used to.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'retire' },
              { k: 'stat', stat: 'stress', delta: -16 },
              { k: 'stat', stat: 'energy', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: -12 },
              { k: 'memory', title: 'Retired', tone: 'milestone', category: 'life', weight: 10 },
              { k: 'triggerEvent', eventId: 'retirement.newStructure', afterYears: 1 },
            ],
          },
        ],
      },
      {
        id: 'continue',
        label: 'Keep working',
        hint: 'safe',
        tags: ['work'],
        outcomes: [
          {
            weight: 1,
            label: 'Carried on',
            narrative: 'The work is the structure. Removing it is not obviously an improvement.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -8 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'flag', flag: 'workedPastRetirement', value: true },
            ],
          },
        ],
      },
      {
        id: 'partial',
        label: 'Work less, stop later',
        hint: 'safe',
        tags: ['balance', 'retirement'],
        outcomes: [
          {
            weight: 1,
            label: 'Phased out',
            narrative: 'Three days a week. The best available version of both options.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 0.6 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -26 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'stat', stat: 'energy', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: 4 },
              { k: 'stat', stat: 'stress', delta: -12 },
            ],
          },
        ],
      },
    ],
  },
];

export const RETIREMENT_EVENTS: GameEvent[] = [
  {
    id: 'retirement.newStructure',
    title: 'Nobody needs you on Monday',
    description: 'The absence of obligation is not the same as freedom. It has to be built.',
    category: 'life',
    pools: ['pool.retirement', 'pool.retired.leisure'],
    weight: 8,
    unique: true,
    requires: [{ k: 'careerStatus', anyOf: ['retired'] }],
    choices: [
      {
        id: 'purpose',
        label: 'Build something to do',
        detail: 'Volunteering, teaching, a serious project.',
        hint: 'safe',
        tags: ['contribution', 'purpose'],
        outcomes: [
          {
            weight: 1,
            label: 'Found a structure',
            narrative: 'Two commitments a week and a reason to be somewhere. It transformed the decade.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 22 },
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'stat', stat: 'health', delta: 5 },
              { k: 'counter', name: 'contributions', delta: 2 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 30 },
            ],
          },
        ],
      },
      {
        id: 'travel',
        label: 'Go and see things',
        hint: 'safe',
        requires: [{ k: 'num', path: 'netWorth', op: '>=', value: 120000 }],
        showWhenLocked: true,
        tags: ['exploration', 'leisure'],
        outcomes: [
          {
            weight: 1,
            label: 'Travelled',
            narrative: 'The trips you postponed for forty years, taken with slightly worse knees.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -8, note: 'travel' },
              { k: 'counter', name: 'travelTrips', delta: 3 },
              { k: 'stat', stat: 'happiness', delta: 20 },
              { k: 'aspiration', op: 'progress', defId: 'dream.travel', delta: 50 },
              { k: 'regret', op: 'resolve', id: 'regret.travel' },
            ],
          },
        ],
      },
      {
        id: 'drift',
        label: 'Take it as it comes',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Drifted',
            narrative: 'Television, admin, and a slow narrowing of the week.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'purpose', delta: -16 },
              { k: 'stat', stat: 'happiness', delta: -8 },
              { k: 'stat', stat: 'health', delta: -6 },
              { k: 'habit', habit: 'exercise', delta: -10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'retirement.moneyRuns',
    title: 'The arithmetic of drawdown',
    description: 'The portfolio has to last an unknown number of years. Spending more now means less later.',
    category: 'finance',
    pools: ['pool.finance.retire'],
    weight: 7,
    cooldown: 5,
    requires: [{ k: 'careerStatus', anyOf: ['retired'] }],
    choices: [
      {
        id: 'careful',
        label: 'Spend cautiously',
        hint: 'safe',
        tags: ['security'],
        outcomes: [
          {
            weight: 1,
            label: 'Lived below the line',
            narrative: 'The money will outlast you. Some of the years were quieter than they needed to be.',
            tone: 'neutral',
            effects: [
              { k: 'lifestyle', delta: -1 },
              { k: 'stat', stat: 'stress', delta: -6 },
              { k: 'stat', stat: 'happiness', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'enjoy',
        label: 'Spend it while you can use it',
        hint: 'mixed',
        requires: [{ k: 'num', path: 'netWorth', op: '>=', value: 200000 }],
        showWhenLocked: true,
        tags: ['leisure'],
        outcomes: [
          {
            weight: 1,
            label: 'Spent the good years',
            narrative: 'Health is the binding constraint, not money. You noticed in time.',
            tone: 'positive',
            effects: [
              { k: 'lifestyle', delta: 2 },
              { k: 'stat', stat: 'happiness', delta: 16 },
              { k: 'counter', name: 'travelTrips', delta: 1 },
              { k: 'relationshipAll', dim: 'closeness', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'give',
        label: 'Give it away now',
        hint: 'safe',
        requires: [{ k: 'num', path: 'netWorth', op: '>=', value: 250000 }],
        showWhenLocked: true,
        tags: ['legacy', 'contribution'],
        outcomes: [
          {
            weight: 1,
            label: 'Gave while alive',
            narrative: 'Deposits for grandchildren, a bursary with your name off it. Better spent now than bequeathed later.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -18, note: 'giving' },
              { k: 'counter', name: 'contributions', delta: 3 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 45 },
              { k: 'stat', stat: 'purpose', delta: 18 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 12 },
              { k: 'achievement', id: 'ach.give', label: 'Gave it away while alive', category: 'legacy' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'legacy.whatRemains',
    title: 'What stays behind',
    description: 'Not the money. The things that will still be happening without you.',
    category: 'legacy',
    pools: ['pool.legacy'],
    weight: 8,
    cooldown: 4,
    requires: [{ k: 'age', min: 58 }],
    choices: [
      {
        id: 'record',
        label: 'Write it down',
        detail: 'Letters, a memoir, the family history nobody else knows.',
        hint: 'safe',
        tags: ['legacy', 'creative'],
        outcomes: [
          {
            weight: 1,
            label: 'Left a record',
            narrative: 'Ninety pages that your grandchildren will read exactly once, carefully.',
            tone: 'positive',
            effects: [
              { k: 'counter', name: 'creativeWorks', delta: 1 },
              { k: 'counter', name: 'contributions', delta: 1 },
              { k: 'stat', stat: 'purpose', delta: 12 },
              { k: 'aspiration', op: 'progress', defId: 'dream.craft', delta: 30 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'people',
        label: 'Repair what is unrepaired',
        hint: 'mixed',
        tags: ['relationship', 'courage'],
        outcomes: [
          {
            weight: 64,
            label: 'Made it right',
            narrative: 'One phone call, thirty years late, and a conversation that mattered to you both.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', dim: 'conflict', delta: -20 },
              { k: 'relationshipAll', dim: 'trust', delta: 12 },
              { k: 'regret', op: 'resolve', id: 'regret.familyMoney' },
              { k: 'regret', op: 'resolve', id: 'regret.absent' },
              { k: 'regret', op: 'resolve', id: 'regret.friend' },
              { k: 'stat', stat: 'happiness', delta: 16 },
              { k: 'stat', stat: 'purpose', delta: 10 },
            ],
          },
          {
            weight: 36,
            label: 'They were not interested',
            narrative: 'You offered. That part was in your control and you did it.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 4 },
              { k: 'stat', stat: 'purpose', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'nothing',
        label: 'Let it be what it is',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Left it alone',
            narrative: 'Some people do not want the conversation. Some of them are you.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'stress', delta: -4 }],
          },
        ],
      },
    ],
  },
  {
    id: 'legacy.finalYears',
    title: 'Later than late',
    description: 'The days are small and mostly good. There is not much left to arrange.',
    category: 'legacy',
    pools: ['pool.retired.leisure', 'pool.health.old'],
    weight: 8,
    cooldown: 2,
    requires: [{ k: 'age', min: 78 }],
    choices: [
      {
        id: 'people',
        label: 'Spend it with people',
        hint: 'safe',
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'Surrounded',
            narrative: 'A full room most weekends. This is the measure that turns out to matter.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'relationshipAll', dim: 'closeness', delta: 8 },
              { k: 'stat', stat: 'purpose', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'quiet',
        label: 'Spend it quietly',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Peaceful',
            narrative: 'Books, a garden, the radio. Entirely sufficient.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'stat', stat: 'stress', delta: -8 },
            ],
          },
        ],
      },
    ],
  },
];
